// src/components/CustomMenu.tsx
import React, { useCallback, useEffect, useState } from 'react';
import type { MenuDataItem } from '@ant-design/pro-layout';
import { useTranslation } from 'react-i18next';

import styles from './styles.less';

const CustomMenu: React.FC<{ menuData: MenuDataItem[]; defaultDom: React.ReactNode }> = ({
  menuData,
  defaultDom,
}) => {
  const { t, i18n } = useTranslation();
  const [translatedMenuData, setTranslatedMenuData] = useState<MenuDataItem[]>([]);

  // 递归地翻译菜单项
  const translateMenuItems = useCallback(
    (items: MenuDataItem[]): MenuDataItem[] =>
      items
        .filter((item) => item && item.name && item.path && !item.hideInMenu)
        .map((item) => ({
          ...item,
          name: t(item.name ?? ''),
          children: item.children ? translateMenuItems(item.children) : [],
        })),
    [t],
  );

  // 监听语言变化并更新菜单数据
  const languageChangedListener = useCallback(() => {
    setTranslatedMenuData(translateMenuItems(menuData));
  }, [translateMenuItems, menuData]);

  useEffect(() => {
    // 监听语言变化
    i18n.on('languageChanged', languageChangedListener);

    // 初始化翻译
    languageChangedListener();

    return () => {
      i18n.off('languageChanged', languageChangedListener);
    };
  }, [i18n, t, menuData, languageChangedListener, translateMenuItems]);

  return (
    <div className={styles.container}>
      {React.cloneElement(defaultDom as React.ReactElement, {
        ...(defaultDom as React.ReactElement).props,
        menuData: translatedMenuData,
      })}
    </div>
  );
};

export default CustomMenu;
